import type { NavigationItem } from "../app/navigation";
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from "../presentation";

type PlaceholderScreenProps = {
  item: NavigationItem;
};

const placeholderDepthContent = defineDepthContent({
  overview: {
    eyebrow: "Planned product outcome",
    summary: "Understand the purpose, intended user, and complete task this route will support.",
    title: "Outcome and ownership",
    signals: ["Plain-language route purpose", "Primary user and decision", "Next implementation milestone"] as const,
    details: [["User outcome", "A complete task with a clear accountable owner"], ["Current state", "Planned; no live records or actions yet"], ["Next milestone", "Implement the assigned feature packet"]] as const,
  },
  operations: {
    eyebrow: "Planned operational workflow",
    summary: "This route is reserved for a packet-owned workflow with real records, actions, and visible feedback.",
    title: "Workflow and acceptance",
    signals: ["Required task sequence", "Operational actions and states", "Packet acceptance boundary"] as const,
    details: [["Workflow", "Load records → review state → take action → receive feedback"], ["Acceptance", "Real records, role-safe actions, and visible consequences"], ["Delivery boundary", "Owned by the route’s assigned work packet"]] as const,
  },
  architecture: {
    eyebrow: "Planned system boundary",
    summary: "Review the route’s future records, dependencies, ownership boundary, and implementation packet.",
    title: "Records and dependencies",
    signals: ["Owning feature directory", "Required domain relationships", "Dependency and packet identifiers"] as const,
    details: [["Record boundary", "Feature-owned records behind repository contracts"], ["Dependencies", "Domain types, permissions, and deterministic seed relationships"], ["Implementation", "Packet ID, allowed paths, tests, and handoff evidence"]] as const,
  },
});

export function PlaceholderScreen({ item }: PlaceholderScreenProps) {
  const { depth } = useDepth();
  const lens = useDepthValue(placeholderDepthContent);

  return (
    <section className="placeholder-screen" aria-labelledby="placeholder-title">
      <div>
        <p className="eyebrow">{lens.eyebrow}</p>
        <h1 id="placeholder-title">{item.label}</h1>
        <p>{lens.summary}</p>
      </div>
      <DepthBrief summary={lens.summary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide contextLabel={`${item.label} fields`} surface="placeholder" />
      <section className={`depth-surface ${depth === "overview" ? "overview-action-grid" : "architecture-record-grid"}`} aria-label={`${item.label} perspective details`}>
        {lens.details.map(([label, value]) => (
          <article className={depth === "overview" ? "overview-action-card" : "architecture-record-card"} key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </section>
      <p className="placeholder-screen__packet">
        Implementation contract: <strong>see the assigned packet under CROC-OPS/work/</strong>
      </p>
    </section>
  );
}
